import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import './SchoolsGridTable.scss';
import { schoolsActions } from 'store/actions/actions';
import { hasAccessTo } from 'store/selectors/auth';
import { getColumns } from './ColumnFormatters';
import RemoteTable from 'components/shared/RemoteTable/RemoteTable';
import EditSchoolModal from 'components/modals/EditSchoolModal/EditSchoolModal';
import { statuses } from 'constants/statuses';
import { schoolCountries } from 'constants/schoolRegions';
import { Button } from 'react-bootstrap';
import { SchoolForm } from 'features/schools/SchoolForm';
import MantineProvider from 'providers/MantineProvider';

const SchoolsGridTable = ({ onFileUploadClickHandler }) => {
  const [clickedFilterValue, setClickedFilterValue] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [queryParams, setQueryParams] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creatingOrganization, setCreatingOrganization] = useState(null);

  const {
    schools,
    total,
    totalUserCount,
    isLoadingSchools,
    isOwner,
    isSchoolManager,
  } = useSelector((state) => ({
    schools: state.schools.schools,
    tags: state.schools.tags,
    totalUserCount: state.schools.total_user_count,
    total: state.schools.total,
    isLoadingSchools: state.schools.isLoadingSchools,
    isOwner: hasAccessTo(state.auth.scopes, ['owner']),
    isSchoolManager: hasAccessTo(state.auth.scopes, ['school_management']),
  }));

  const dispatch = useDispatch();
  const hasKnownTotal = Number.isFinite(total) && total >= 0;
  const pageCount = hasKnownTotal
    ? Math.ceil(total / (queryParams?.limit || 10))
    : null;
  const currentPage = queryParams?.page || 1;

  const tableOptions = {
    hasToggle: false,
    hasSearch: true,
    searchPlaceholder: 'Search for any field...',
    onCreateButtonClick:
      isSchoolManager && (() => setCreatingOrganization('high_school')),
    createButtonLabel: 'Create high school',
    additionalActions: isSchoolManager && (
      <Button
        variant="danger"
        className="create-button"
        onClick={() => setCreatingOrganization('college')}
      >
        Create college
      </Button>
    ),
    shouldDisplayCount: true,
    onFileUploadButtonClick: onFileUploadClickHandler,
    hasDropdown: true,
    dropdownOptions: statuses.map((st) => ({ value: st.id, label: st.name })),
    dropdownSelectedOption: 'live',
    dropdownFilterField: 'status',
    dropdownFilters: [
      {
        field: 'organization_type',
        label: 'Organization type',
        allLabel: 'All org types',
        options: [
          { value: 'high_school', label: 'HS' },
          { value: 'college', label: 'College' },
        ],
      },
      {
        field: 'country',
        label: 'Country',
        allLabel: 'All countries',
        options: schoolCountries,
      },
    ],
    counterComponent: () => (
      <div className="schools-summary" role="status">
        {isLoadingSchools ? (
          'Loading schools…'
        ) : (
          <>
            <span>
              {hasKnownTotal ? (
                <>
                  <strong>{total.toLocaleString()}</strong>{' '}
                  {total === 1 ? 'school' : 'schools'}
                </>
              ) : (
                'School total unavailable'
              )}
            </span>
            <span className="schools-summary-pages">
              {pageCount === 0
                ? '0 pages'
                : `Page ${currentPage.toLocaleString()}${
                    hasKnownTotal ? ` of ${pageCount.toLocaleString()}` : ''
                  }`}
            </span>
            {isOwner && Number.isFinite(totalUserCount) && (
              <span className="schools-summary-installs">
                {totalUserCount.toLocaleString()} installs
              </span>
            )}
          </>
        )}
      </div>
    ),
  };

  useEffect(() => {
    if (queryParams) {
      loadSchools();
    }
  }, [queryParams]);

  const loadSchools = () =>
    dispatch(schoolsActions.getSchools({ ...queryParams }));

  return (
    <div>
      {creatingOrganization && (
        <MantineProvider>
          <SchoolForm
            createCollege={creatingOrganization === 'college'}
            modalProps={{
              opened: true,
              onClose: () => setCreatingOrganization(null),
            }}
            onSaved={loadSchools}
          />
        </MantineProvider>
      )}
      {selectedSchool && showEditModal && (
        <EditSchoolModal
          hide={() => {
            setShowEditModal(false);
            setSelectedSchool(null);
          }}
          school={selectedSchool}
          onUpdated={loadSchools}
        />
      )}

      <RemoteTable
        keyField="school_name"
        itemName="School"
        options={tableOptions}
        data={schools || []}
        clickedFilterValue={clickedFilterValue}
        setClickedFilterValue={setClickedFilterValue}
        getColumns={() =>
          getColumns({
            isSchoolManager,
            onStatusUpdated: loadSchools,
            onDistrictClick: setClickedFilterValue,
            onActionsClick: (row) => {
              setSelectedSchool(row);
              setShowEditModal(true);
            },
          })
        }
        loading={isLoadingSchools}
        defaultSorted={{
          dataField: 'school_title',
          order: 'asc',
          filter_by: 'status:eq:live',
        }}
        refreshHandler={setQueryParams}
        total={total}
      />
    </div>
  );
};

export default SchoolsGridTable;
